/**
 * Pure tier-band helpers -- phase 6b, Task 5.
 *
 * The five loyalty bands are half-open `[min, max)` on lifetime spend and
 * MUST be contiguous (ADMIN_API.md "Loyalty tiers"): tier 1 starts at 0
 * (ruling A-1), tier n+1 starts where tier n ends, tier 5 is open-ended.
 *
 * `relink` is the ONLY place the UI derives a value, and it does so by
 * COPYING the previous band's max string into the next band's min -- no
 * arithmetic, no parsing. `validateTiers` mirrors the API's `putTiersBody`
 * superRefine rule for rule (same messages, same `tiers.<i>.<field>` keys,
 * `i` indexing the array SORTED by tierNumber) so a bad set is caught
 * before the round-trip and a 422 from the API lands on the same cell.
 * `Number()` appears here only to COMPARE two money strings, exactly as the
 * API's refine does; nothing computed is ever written back.
 */
import type { TierInput, TierRow } from '../types';

export const TIER_COUNT = 5;

/** The API's own copy (admin-loyalty `TIER_OFF_MESSAGE`), ruling A-7. */
export const TIER_OFF_MESSAGE = 'Switching a band off leaves its customers with no tier — move the band boundaries instead.';

/** The API's `dailyRedemptionLimit: z.number().int().min(1).nullable()` -- blank (null) is unlimited, 0 is refused. */
export const DAILY_LIMIT_MESSAGE = 'must be at least 1';
/** ...and `.int()`: a typed `1.5` is reported, never silently truncated to 1 (fix wave minor 10). */
export const DAILY_LIMIT_WHOLE_MESSAGE = 'must be a whole number';

/** Same pattern as the API's `moneyString` (schemas/shared.ts). */
const MONEY = /^\d+(\.\d{1,2})?$/;

export const sortTiers = <T extends { tierNumber: number }>(tiers: T[]): T[] => [...tiers].sort((a, b) => a.tierNumber - b.tierNumber);

/**
 * Re-derive the linked boundaries after an edit: tier 1 min is `'0.00'`,
 * tier n+1 min is tier n max (string copy; a null max leaves the next min
 * as it was, since there is nothing to copy), tier 5 max is `null`.
 * Returns a new sorted array; the input is not mutated.
 */
export function relink(tiers: TierInput[]): TierInput[] {
  const t = sortTiers(tiers);
  return t.map((x, i) => ({
    ...x,
    minLifetimeSpend: i === 0 ? '0.00' : (t[i - 1].maxLifetimeSpend ?? x.minLifetimeSpend),
    maxLifetimeSpend: i === TIER_COUNT - 1 ? null : x.maxLifetimeSpend,
  }));
}

/**
 * The API's `putTiersBody` refine, run locally. Keys are `tiers.<i>.<field>`
 * on the SORTED array; a whole-body problem lands under `''`. One
 * deliberate difference: the API reports "reward must be > 0" at body level
 * (no path); here it is keyed to `tiers.<i>.rewardAmount` so it lands on the
 * cell. Non-money strings (the API would answer 400 from Ajv) are reported
 * on their cell too, so the form never sends them -- as is a
 * `dailyRedemptionLimit` below 1 or not a whole number (the API's
 * `z.number().int().min(1)`; ADMIN_API.md: never send 0 for "unlimited",
 * that is `null`).
 */
export function validateTiers(tiers: TierInput[]): Record<string, string> {
  const errors: Record<string, string> = {};
  const add = (key: string, message: string) => {
    if (!errors[key]) errors[key] = message;
  };
  const t = sortTiers(tiers);
  if (t.map((x) => x.tierNumber).join() !== '1,2,3,4,5') {
    add('', 'tiers must be exactly 1..5');
    return errors;
  }

  t.forEach((x, i) => {
    if (!MONEY.test(x.minLifetimeSpend)) add(`tiers.${i}.minLifetimeSpend`, 'money as a 2dp string, e.g. "50.00"');
    if (x.maxLifetimeSpend !== null && !MONEY.test(x.maxLifetimeSpend)) add(`tiers.${i}.maxLifetimeSpend`, 'money as a 2dp string, e.g. "50.00"');
    if (!MONEY.test(x.rewardAmount)) add(`tiers.${i}.rewardAmount`, 'money as a 2dp string, e.g. "50.00"');
  });

  if (Number(t[0].minLifetimeSpend) !== 0) add('tiers.0.minLifetimeSpend', 'tier 1 starts at 0');
  if (t[4].maxLifetimeSpend !== null) add('tiers.4.maxLifetimeSpend', 'tier 5 is open-ended (null)');
  for (let i = 0; i < 4; i += 1) {
    const max = t[i].maxLifetimeSpend;
    if (max === null) add(`tiers.${i}.maxLifetimeSpend`, 'only tier 5 may be open-ended');
    else if (Number(max) <= Number(t[i].minLifetimeSpend)) add(`tiers.${i}.maxLifetimeSpend`, 'max must exceed min');
    else if (Number(max) !== Number(t[i + 1].minLifetimeSpend)) add(`tiers.${i + 1}.minLifetimeSpend`, `tier ${i + 2} must start where tier ${i + 1} ends`);
  }
  t.forEach((x, i) => {
    if (!(Number(x.rewardAmount) > 0)) add(`tiers.${i}.rewardAmount`, 'reward must be > 0');
  });
  t.forEach((x, i) => {
    const limit = x.dailyRedemptionLimit;
    if (limit === null) return;
    if (!Number.isInteger(limit)) add(`tiers.${i}.dailyRedemptionLimit`, DAILY_LIMIT_WHOLE_MESSAGE);
    else if (limit < 1) add(`tiers.${i}.dailyRedemptionLimit`, DAILY_LIMIT_MESSAGE);
  });
  t.forEach((x, i) => {
    if (!x.active) add(`tiers.${i}.active`, TIER_OFF_MESSAGE);
  });
  return errors;
}

/**
 * GET rows -> PUT inputs: drop the joined promotion fields and force
 * `active: true` (ruling A-7 -- the only value the API accepts; the chip on
 * screen is read-only, there is no toggle to carry).
 */
export function toTierInputs(rows: TierRow[]): TierInput[] {
  return sortTiers(rows).map((r) => ({
    tierNumber: r.tierNumber,
    minLifetimeSpend: r.minLifetimeSpend,
    maxLifetimeSpend: r.maxLifetimeSpend,
    rewardAmount: r.rewardAmount,
    dailyRedemptionLimit: r.dailyRedemptionLimit,
    active: true,
  }));
}

/** `rewardAmount` and the tier promotion's `discountValue` are written atomically; if they differ the row is out of sync. */
export const hasDrift = (row: TierRow): boolean => row.promotionDiscountValue !== row.rewardAmount;
